import { FC, useMemo, useState } from 'react';
import { PortalHolding } from 'views/MyCellar/hooks/usePortalHoldings';
import { useTheme } from '../../../../context/ThemeContext';

// Theme colors matching other page components
const lightThemeColors = {
  background: '#FFFFFF',
  cardBackground: '#F8FAFC',
  text: '#000000',
  textSecondary: '#666666',
  border: '#E2E8F0',
};

const darkThemeColors = {
  background: '#0A1628',
  cardBackground: '#0F1E32',
  text: '#FFFFFF',
  textSecondary: '#8B9CB3',
  border: '#1E3A50',
};

interface PerformanceMomentumProps {
  holdings: PortalHolding[];
  isLoading?: boolean;
}

const BarSegment: FC<{
  widthPct: number;
  color: string;
  label: string;
  count: number;
  pct: number;
  themeColors: typeof lightThemeColors;
  isDark: boolean;
  isFirst: boolean;
  isLast: boolean;
}> = ({ widthPct, color, label, count, pct, themeColors, isDark, isFirst, isLast }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="h-full transition-all duration-200 relative cursor-pointer"
      style={{
        width: `${widthPct}%`,
        backgroundColor: color,
        filter: isHovered ? 'brightness(1.15)' : 'none',
        borderRadius: `${isFirst ? '6px' : '0'} ${isLast ? '6px' : '0'} ${isLast ? '6px' : '0'} ${
          isFirst ? '6px' : '0'
        }`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isHovered && (
        <div
          className="absolute left-1/2 transform -translate-x-1/2 px-4 py-3 rounded-lg shadow-lg text-center pointer-events-none z-10 whitespace-nowrap"
          style={{
            bottom: '100%',
            marginBottom: '6px',
            backgroundColor: isDark ? themeColors.cardBackground : '#FFFFFF',
            border: `1px solid ${themeColors.border}`,
          }}
        >
          <div className="text-base font-medium" style={{ color }}>
            {label}
          </div>
          <div className="text-sm" style={{ color: themeColors.textSecondary }}>
            {count} holdings ({pct.toFixed(1)}%)
          </div>
        </div>
      )}
    </div>
  );
};

const PerformanceMomentum: FC<PerformanceMomentumProps> = ({ holdings, isLoading }) => {
  const { isDark } = useTheme();
  const themeColors = isDark ? darkThemeColors : lightThemeColors;

  const momentumData = useMemo(() => {
    if (!holdings || holdings.length === 0) {
      return [];
    }

    const threshold = 0.5; // ±0.5% threshold for flat

    const categorize = (changePercent: number | null | undefined): 'up' | 'flat' | 'down' => {
      if (changePercent === null || changePercent === undefined || Number.isNaN(changePercent)) {
        return 'flat';
      }
      if (changePercent > threshold) return 'up';
      if (changePercent < -threshold) return 'down';
      return 'flat';
    };

    const periods: { key: keyof PortalHolding; label: string }[] = [
      { key: 'changePercent1Month', label: '1M' },
      { key: 'changePercent3Months', label: '3M' },
      { key: 'changePercent6Months', label: '6M' },
      { key: 'changePercent12Months', label: '12M' },
    ];

    return periods.map(({ key, label }) => {
      let up = 0;
      let flat = 0;
      let down = 0;

      holdings.forEach((h) => {
        const cat = categorize(h[key] as number);
        if (cat === 'up') up += 1;
        else if (cat === 'down') down += 1;
        else flat += 1;
      });

      return { period: label, up, flat, down, total: holdings.length };
    });
  }, [holdings]);

  // Colors
  const upColor = isDark ? '#10B981' : '#059669'; // Green matching theme
  const flatColor = isDark ? '#64748B' : '#94A3B8'; // Slate
  const downColor = isDark ? '#EF4444' : '#DC2626'; // Red matching theme

  if (isLoading) {
    return (
      <div
        className="flex-1 flex flex-col rounded-lg overflow-hidden transition-colors animate-pulse"
        style={{ backgroundColor: themeColors.background }}
      >
        <div className="px-5 pt-5 pb-3">
          <div className="h-6 w-56 rounded mb-2" style={{ backgroundColor: themeColors.cardBackground }} />
          <div className="h-4 w-80 rounded" style={{ backgroundColor: themeColors.cardBackground }} />
        </div>
        <div className="px-5 pb-5 space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-8 rounded" style={{ backgroundColor: themeColors.cardBackground }} />
          ))}
        </div>
      </div>
    );
  }

  if (!holdings || holdings.length === 0) {
    return null;
  }

  return (
    <div
      className="flex-1 flex flex-col rounded-lg overflow-hidden transition-colors"
      style={{ backgroundColor: themeColors.background }}
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center mb-2">
          <div className="w-4 h-4 rounded-full bg-gradient-to-r from-green-500 to-red-500" />
          <h3 className="text-base sm:text-lg" style={{ color: themeColors.text }}>
            Performance Momentum
          </h3>
        </div>
        <p className="text-sm" style={{ color: themeColors.textSecondary }}>
          How many holdings moved up, stayed flat, or declined. Click legend to view wines.
        </p>
      </div>

      {/* Bars */}
      <div className="px-5 pb-5 space-y-4 mt-3">
        {momentumData.map((data) => {
          const upPct = data.total > 0 ? (data.up / data.total) * 100 : 0;
          const flatPct = data.total > 0 ? (data.flat / data.total) * 100 : 0;
          const downPct = data.total > 0 ? (data.down / data.total) * 100 : 0;

          return (
            <div key={data.period} className="flex items-center gap-4">
              <span className="text-sm font-medium w-10" style={{ color: themeColors.text }}>
                {data.period}
              </span>
              <div className="flex-1 flex h-10 relative">
                {(() => {
                  const segments = [
                    upPct > 0 && { widthPct: upPct, color: upColor, label: 'Up', count: data.up, pct: upPct },
                    flatPct > 0 && {
                      widthPct: flatPct,
                      color: flatColor,
                      label: 'Flat',
                      count: data.flat,
                      pct: flatPct,
                    },
                    downPct > 0 && {
                      widthPct: downPct,
                      color: downColor,
                      label: 'Down',
                      count: data.down,
                      pct: downPct,
                    },
                  ].filter(Boolean) as { widthPct: number; color: string; label: string; count: number; pct: number }[];

                  return segments.map((seg, i) => (
                    <BarSegment
                      key={seg.label}
                      widthPct={seg.widthPct}
                      color={seg.color}
                      label={seg.label}
                      count={seg.count}
                      pct={seg.pct}
                      themeColors={themeColors}
                      isDark={isDark}
                      isFirst={i === 0}
                      isLast={i === segments.length - 1}
                    />
                  ));
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div
        className="flex items-center gap-5 px-5 py-3 text-sm border-t"
        style={{ borderColor: themeColors.border, color: themeColors.textSecondary }}
      >
        <div className="flex items-center gap-2">
          <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: upColor }} />
          <span>Up (&gt;0.5%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: flatColor }} />
          <span>Flat (±0.5%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: downColor }} />
          <span>Down (&lt;-0.5%)</span>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMomentum;
